import {Card,Checkbox,Container,Table,TableBody,TableCell,TableContainer,TablePagination,TableRow,} from '@mui/material';
import { debounce } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import PageHeader from 'components/PageHeader/PageHeader';
import Popup from 'components/Popup/Popup';
import MoreMenu from 'components/table/MoreMenu';
import SearchNotFound from 'components/table/SearchNoFound';
import TableHeader from 'components/table/TableHeader';
import TableToolBar from 'components/table/TableToolbar';
import { getImpactReportModelTableHead } from 'constans/table-headers';
import SnackbarContext from 'contexts/snackbarContext';
import {handleClick,handleSelectAllClick,sortByColumn,} from 'services/list.service';

import {deleteImpactReportModel,deleteImpactReportModelBulk,getImpactReportModels,searchImpactReportModels,} from 'services/impactReportModel.services';

import { ImpactReportModelType } from 'types/impactReportModel.type';

export default function impactReportPage() {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [selected, setSelected] = useState<string[]>([]);
  const [orderBy, setOrderBy] = useState('name');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const { handleOpen } = useContext(SnackbarContext);
  const [impactReportList, setImpactReportModelList] = useState<ImpactReportModelType[]>([]);
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    getImpactReportModelsHandler();
  }, []);

  const getImpactReportModelsHandler = () => {
    getImpactReportModels().then((response) => {
      setImpactReportModelList(response.data);
    });
  };

  const searchFilter = useCallback(
    debounce((filterName) => {
      if (filterName) {
        searchImpactReportModels(filterName).then((response) => {
          setImpactReportModelList(response.data);
        });
      } else {
        getImpactReportModelsHandler();
      }
    }, 300),
    [],
  );

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteImpactReport = (impactReportId: string) => {
    deleteImpactReportModel(impactReportId)
      .then(() => {
        setImpactReportModelList([...impactReportList].filter(({ id }) => id !== impactReportId));
        handleSelectAllClick(false, impactReportList);
        setOpenModal(false);
        handleOpen(t('impactReportModelPage.impactReportModelDeletedSuccessMessage'), 'success');
      })
      .catch(({ data }) => {
        handleOpen(data.message);
      });
  };

  const handleDeleteImpactReportBulk = (impactReportIds: string[]) => {
    deleteImpactReportModelBulk(impactReportIds)
      .then(() => {
        setImpactReportModelList(impactReportList.filter(({ id }) => !impactReportIds.includes(id)));
        handleSelectAllClick(false, impactReportList);
        setOpenModal(false);
        handleOpen(t('impactReportModelPage.impactReportModelsDeletedSuccessMessage'), 'success');
      })
      .catch(({ data }) => {
        handleOpen(data.message);
      });
  };

  const deleteImpactReportAction = () => {
    if (selected.length > 1) {
      handleDeleteImpactReportBulk(selected);
    } else {
      handleDeleteImpactReport(selected[0]);
    }
  };

  const openModalHandler = (id?: string) => {
    setOpenModal(true);
    if (id) {
      setSelected([id]);
    }
  };

  const sortedImpactReportList = sortByColumn(
    impactReportList,
    orderBy as keyof ImpactReportModelType,
    order,
  );

  const impactReportModelTableHead = useMemo(getImpactReportModelTableHead, [language]);

  return (
    <Container>
      <PageHeader title={t('impactReportModelPage.title')} buttonTitle={t('impactReportModelPage.buttonTitle').toString()} hasButton hasBackButton/>
      <Card>
        <TableToolBar
          numSelected={selected.length}
          onFilterName={searchFilter}
          deleteHandler={() => openModalHandler()}
        />

        <TableContainer>
          <Table>
            {
              <TableHeader
                order={order}
                orderBy={orderBy}
                headLabel={impactReportModelTableHead}
                rowCount={impactReportList.length}
                numSelected={selected.length}
                onRequestSort={handleRequestSort}
                onSelectAllClick={(checked) =>
                  setSelected(handleSelectAllClick(checked, impactReportList))
                }
              />
            }
            <TableBody>
              {sortedImpactReportList
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => {
                  const {
                    id,
                    name,
                    description
                  } = row;
                  const isItemSelected = selected.indexOf(id) !== -1;

                  return (
                    <TableRow
                      hover
                      key={id}
                      tabIndex={-1}
                      role="checkbox"
                      selected={isItemSelected}
                      aria-checked={isItemSelected}
                    >

                      <TableCell padding="checkbox"><Checkbox checked={isItemSelected}onClick={() => setSelected(handleClick(id, selected))}/></TableCell>
                      <TableCell align="left">{name}</TableCell>
                      <TableCell align="left">{description }</TableCell>
                      <TableCell align="right">
                        <MoreMenu
                          onDelete={() => openModalHandler(id)}
                          id={id}
                        />
                      </TableCell>

                    </TableRow>
                  );
                })}
            </TableBody>
            {!impactReportList.length && (
              <TableBody>
                <TableRow>
                  <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                    <SearchNotFound />
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={impactReportList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, page) => setPage(page)}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t('pagination.rowsPerPage')}
        />
      </Card>
      <Popup
        type="delete"
        open={openModal}
        onConfirm={deleteImpactReportAction}
        handleClose={() => setOpenModal(false)}
      />
    </Container>
  );
}
